import { watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAiStore } from '@/stores/ai';
import {
  setShellAssistantActions,
  clearShellAssistantActions,
} from '@/webshell-embed/shell-assistant-provider';
import { isEmbedded } from '@/webshell-embed/embedded';
import { useFeature } from './use-feature';
import { useToolbarNavigation } from './use-toolbar-navigation';
import { useScreenshot } from './use-screenshot';

const CONTEXT_READY_TIMEOUT_MS = 15000;

// Sent to the LLM when the user stops the agent chain mid-run.
const STOP_SUMMARY_PROMPT =
  '[Tool] The user has requested to stop. Please briefly summarise the actions you have completed so far and let the user know you have stopped.';

/**
 * Composable that handles AI agent actions for a page using native tool calling.
 *
 * Call this once in the page's `setup()` function.
 *
 * Usage:
 *
 *   const { getToolSchemas } = useAiAgentActions({
 *     allowScenarioWorkflowNavigation: true,
 *     actions: {
 *       'rename-workpackage': {
 *         description: 'Rename the currently selected workpackage.',
 *         parameters: {
 *           type: 'object',
 *           properties: { value: { type: 'string', description: 'The new name' } },
 *           required: ['value'],
 *         },
 *         handler: async ({ value }) => {
 *           await workpackagesStore.updateWorkpackage({ name: value });
 *           return '[Tool] Action succeeded: workpackage renamed.';
 *         },
 *       },
 *     },
 *   });
 *
 * The `parameters` field follows JSON Schema (type: 'object'). If omitted, a default
 * single-string `value` parameter is used for backward compatibility with existing
 * handlers that receive a single value argument.
 *
 * The composable auto-registers a tool schemas factory with the store so tool schemas
 * are resolved lazily at message-send time (important for the navigate tool whose
 * available routes change as the Toolbar mounts/unmounts).
 *
 * @param {Object} options
 * @param {boolean} [options.allowScenarioWorkflowNavigation]
 * @param {Record<string, {
 *   description: string,
 *   parameters?: object,
 *   workingLabel?: string,
 *   readOnly?: boolean,
 *   confirmationLabel?: string | ((args: object) => string),
 *   handler: Function
 * }>} [options.actions] - Action definitions.
 *   - `readOnly: true`        – tool executes without a confirmation prompt (e.g. data fetches).
 *   - `confirmationLabel`     – human-readable sentence shown in the confirmation prompt
 *                               (e.g. `(args) => \`rename workpackage to "${args.value}"\``). If omitted,
 *                               the tool name is used as a fallback. Should be a short lowercase phrase
 *                               completing "The agent wants to ...", e.g. `'save all cann group changes'`
 *                               or `(args) => \`rename "${args.currentName}" to "${args.newName}"\``.
 * @param {() => string[]} [options.getExclude] - Called lazily at send-time. Return tool names to
 *   omit from the schema (e.g. write actions when editing is disabled, or tools the current user
 *   lacks permission to use). Excluded tools are invisible to the LLM — it will not attempt them.
 * @param {() => Record<string, string>} [options.getSupplementary] - Called lazily at send-time.
 *   Return a map of toolName → extra context string appended to that tool's description.
 *   Use this for per-action dynamic context (e.g. listing current custom attribute names for
 *   delete-custom-attribute). The value is appended after a newline.
 * @returns {{ getToolSchemas: () => Array }}
 */

// Default parameters schema for actions that don't specify one (backward compatibility).
// TODO: the three remaining callers in AttributeEditor.vue and VarietyGroups.vue
// should add explicit parameters schemas and stop relying on this default.
// IMPORTANT: Do NOT pass `parameters: {}` from an action definition — an empty object is
// truthy and bypasses this default, resulting in `input_schema: {}` being sent to Anthropic,
// which rejects it with a 400 ("input_schema.type: Field required").
// For no-argument actions, always use the explicit form:
//   parameters: { type: 'object', properties: {}, required: [] }
const DEFAULT_PARAMETERS = {
  type: 'object',
  properties: {
    value: { type: 'string', description: 'The action value' },
  },
  required: ['value'],
};

export function useAiAgentActions(options = {}) {
  const {
    allowScenarioWorkflowNavigation = false,
    actions = {},
    getExclude = null,
    getSupplementary = null,
  } = options;

  // Build handler map, action metadata, and read-only / label lookups.
  const handlers = {};
  const pageActionDefs = [];
  // Tools that execute silently without a confirmation prompt.
  // capture_screenshot, render_chart and render_table are always read-only; page actions opt-in via readOnly: true.
  const readOnlyTools = new Set(['capture_screenshot', 'render_chart', 'render_table']);
  // Label factories for the confirmation prompt, keyed by tool name.
  const labelFactories = {};

  Object.entries(actions).forEach(([name, def]) => {
    handlers[name] = def.handler;
    pageActionDefs.push({
      name,
      description: def.description,
      parameters: def.parameters || DEFAULT_PARAMETERS,
      workingLabel: def.workingLabel || null,
    });
    if (def.readOnly) readOnlyTools.add(name);
    if (def.confirmationLabel) labelFactories[name] = def.confirmationLabel;
  });

  const aiStore = useAiStore();
  const { aiAgentModeEnabled, isAgentEnabledForSection } = useFeature();

  // If this section is not in the agentEnabledSections allowlist, strip all page-specific
  // AI setup so the panel behaves exactly like a non-wired page (general questions only).
  // Runs in onMounted + nextTick so it fires after the page's own mounted() hook has called
  // enableSidePanel() — giving us the section key without needing it passed as a parameter.
  onMounted(async () => {
    await nextTick();
    const currentSection = aiStore.sidePanel.context?.section;
    if (!isAgentEnabledForSection(currentSection)) {
      aiStore.enableSidePanel(null);
      aiStore.setSidePanelContextFactory(null);
      aiStore.setToolSchemasFactory(() => []);
    }
  });

  const router = useRouter();
  const route = useRoute();
  const { captureViewport } = useScreenshot();
  const { navigationTargets, getAvailableNavigationTargets } = allowScenarioWorkflowNavigation
    ? useToolbarNavigation()
    : { navigationTargets: { value: [] }, getAvailableNavigationTargets: null };

  /**
   * Returns a Promise that resolves when aiStore.sidePanel.contextReady becomes true.
   * Rejects with a timeout error after CONTEXT_READY_TIMEOUT_MS.
   */
  function waitForContextReady() {
    if (aiStore.sidePanel.contextReady) return Promise.resolve();
    return new Promise((resolve, reject) => {
      let unwatch;
      const timeout = setTimeout(() => {
        if (unwatch) unwatch();
        reject(new Error('Timed out waiting for page context to be ready.'));
      }, CONTEXT_READY_TIMEOUT_MS);
      unwatch = watch(
        () => aiStore.sidePanel.contextReady,
        ready => {
          if (ready) {
            clearTimeout(timeout);
            unwatch();
            resolve();
          }
        },
        { immediate: true }
      );
    });
  }

  /**
   * Returns the canonical tool schemas array for the current state.
   * Includes:
   *   - The built-in `navigate` tool (if allowScenarioWorkflowNavigation and routes are available)
   *   - All registered page-specific actions, minus any excluded by getExclude()
   *
   * Called lazily at message-send time via the auto-registered toolSchemasFactory so that
   * the navigate tool's enum and dynamic exclusions reflect current page/permission state.
   *
   * @returns {Array<{ name: string, description: string, parameters: object }>}
   */
  function getToolSchemas() {
    // Safety check: if the current section is not in the allowlist, return no tools.
    // onMounted handles the full cleanup; this guards the unlikely case where sendMessage
    // is called before onMounted has fired.
    if (!isAgentEnabledForSection(aiStore.sidePanel.context?.section)) return [];

    // Resolve dynamic options at call-time so exclusions and supplementary context
    // always reflect current page state and user permissions.
    // Merge page-level exclusions with session-level exclusions (tools the user has permanently
    // declined this session are hidden from the LLM so it stops attempting them).
    let pageExclude = [];
    try {
      pageExclude = getExclude ? getExclude() : [];
    } catch (e) {
      console.error('[AI] Error in getExclude callback:', e);
    }
    const exclude = [...pageExclude, ...aiStore.sessionExclusions];

    let supplementary = {};
    try {
      supplementary = getSupplementary ? getSupplementary() : {};
    } catch (e) {
      console.error('[AI] Error in getSupplementary callback:', e);
    }

    const schemas = [];

    // Built-in navigate tool — dynamic: only included when routes are available
    if (getAvailableNavigationTargets) {
      const routes = getAvailableNavigationTargets();
      if (routes.length > 0) {
        const availableLabels = routes.map(r => r.label);
        schemas.push({
          name: 'navigate',
          workingLabel: 'Navigating',
          description:
            'Navigate to another section of the scenario workflow. ' +
            'ONLY use this when the user explicitly asks to go to a different page or section. ' +
            'Do NOT use this proactively after completing an action — stay on the current page and report what you did. ' +
            'If the requested section is not in the available options, ask the user to clarify.',
          parameters: {
            type: 'object',
            properties: {
              value: {
                type: 'string',
                description:
                  'The exact label of the section to navigate to. ' +
                  `Must be one of: ${availableLabels.map(l => `"${l}"`).join(', ')}`,
                enum: availableLabels,
              },
            },
            required: ['value'],
          },
        });
      }
    }

    // Built-in render_table tool — read-only, always included.
    schemas.push({
      name: 'render_table',
      workingLabel: 'Building table...',
      description:
        'Renders an interactive sortable/filterable table inline in the conversation. ' +
        'Use this when presenting structured data where the user would benefit from seeing rows and columns — ' +
        'for example analysis results, breakdowns by attribute value, product comparisons, or summaries. ' +
        'Prefer this over bullet-point lists whenever the data is tabular. ' +
        'The user can sort and filter the inline table, and expand it to a full-screen view with a CSV download option. ' +
        'Provide a title, a columns array (each with field, headerName, and optional type), and a rows array of objects keyed by field.',
      parameters: {
        type: 'object',
        properties: {
          title: {
            type: 'string',
            description: 'A short descriptive title for the table.',
          },
          columns: {
            type: 'array',
            description:
              'Column definitions. Each item must have field (unique key) and headerName (display label). Optionally include type: "text" | "number" | "date".',
            items: {
              type: 'object',
              properties: {
                field: { type: 'string', description: 'Unique key used in row objects.' },
                headerName: {
                  type: 'string',
                  description: 'Column header label shown to the user.',
                },
                type: {
                  type: 'string',
                  enum: ['text', 'number', 'date'],
                  description: 'Optional. Drives filter type. Defaults to text.',
                },
              },
              required: ['field', 'headerName'],
            },
          },
          rows: {
            type: 'array',
            description:
              'Row data. Each item is a plain object keyed by the field values defined in columns.',
            items: { type: 'object' },
          },
        },
        required: ['title', 'columns', 'rows'],
      },
    });

    // Built-in render_chart tool — read-only, always included.
    schemas.push({
      name: 'render_chart',
      workingLabel: 'Drawing chart...',
      description:
        'Renders an interactive chart inline in the conversation. ' +
        'Use this whenever visualising data would be clearer than describing it in text — for example when the user asks for a chart, graph, or visual summary of data. ' +
        'The chart is rendered using Highcharts. Provide a valid Highcharts options object as the chartOptions argument. ' +
        'Always include: chart.type (one of "column", "bar", "line", "pie", "scatter"), title.text, and series[] — each with name and data. ' +
        'For non-pie charts also include xAxis with categories or type. ' +
        'Do not include exporting or credits configuration.',
      parameters: {
        type: 'object',
        properties: {
          chartOptions: {
            type: 'object',
            description:
              'A valid Highcharts options object. Must include chart.type, title.text, and series[] where each item has name and data.',
          },
        },
        required: ['chartOptions'],
      },
    });

    // Built-in capture_screenshot tool — read-only, always included.
    // Description is intentionally restrictive to prevent excessive use.
    schemas.push({
      name: 'capture_screenshot',
      workingLabel: 'Checking the current page...',
      description:
        'Captures a screenshot of the application page for visual UI guidance. ' +
        'LAST RESORT ONLY — your system prompt already contains a structured Current Page Context block with ' +
        'all current data values (scenario name, product counts, errors, status, flags, etc.). ' +
        'If the answer is in that context, use it directly — do NOT screenshot to look up data you already have. ' +
        'ONLY call this when the question is explicitly about visual layout: the user cannot find a UI element, ' +
        'asks where something is on screen, or needs step-by-step guidance that depends on what is currently visible. ' +
        'Do NOT call this for data questions, conceptual questions, or anything present in the page context. ' +
        'Do NOT call this on consecutive turns unless the user confirms the page has changed. ' +
        "Always write a brief acknowledgement before calling (e.g. 'Let me check the current page...').",
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    });

    // Page-specific action schemas
    pageActionDefs.forEach(def => {
      if (exclude.includes(def.name)) return;
      const extra = supplementary[def.name];
      schemas.push({
        name: def.name,
        description: extra ? `${def.description}\n${extra}` : def.description,
        parameters: def.parameters,
        ...(def.workingLabel && { workingLabel: def.workingLabel }),
      });
    });

    // Filter to read-only tools when mutating tools are unavailable:
    //   - Tenant-level flag: aiAgentModeEnabled is false for this tenant
    //   - Session-level block: user chose "No — all actions, this session"
    // Evaluated after all schemas are built so read-only page actions are always included.
    if (!aiAgentModeEnabled.value || aiStore.sessionAllActionsBlocked) {
      return schemas.filter(s => readOnlyTools.has(s.name));
    }

    return schemas;
  }

  // Auto-register the tool schemas factory with the store so schemas are resolved
  // at message-send time rather than eagerly at component mount.
  aiStore.setToolSchemasFactory(() => getToolSchemas());

  // Embedded, publish this page's actions — schema, handler, confirmation metadata — for the
  // shell-owned assistant to call - ADR-012 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/012-ai-assistant-extraction.md
  // The shell runs the confirmation gate; handlers just execute and return their [Tool] string.
  if (isEmbedded()) {
    // The published snapshot, withdrawn again when this page unmounts — the shell's assistant
    // outlives the page, so leaving it in place offers the departed page's handlers.
    let published = null;
    onUnmounted(() => {
      clearShellAssistantActions(published);
      published = null;
    });
    // Gated on the same allowlist as the panel's own tools, and after the same nextTick, so it reads
    // the section the page has just published and runs after the strip above. A section left out of
    // agentEnabledSections has no section prompt behind it: publishing its tools anyway would offer
    // the shell handlers the model has been told nothing about — standalone getToolSchemas() returns
    // [] there, and embedded must behave the same.
    onMounted(async () => {
      await nextTick();
      if (!isAgentEnabledForSection(aiStore.sidePanel.context?.section)) return;
      published = setShellAssistantActions(
        pageActionDefs.map(def => ({
          name: def.name,
          description: def.description,
          parameters: def.parameters,
          readOnly: readOnlyTools.has(def.name),
          // String or function of the call arguments; AO is DOM-mounted in the shell document, so
          // a function survives the handover and the shell calls it when it prompts.
          confirmationLabel: labelFactories[def.name],
          handler: handlers[def.name],
        }))
      );
    });
  }

  /**
   * Resolve the human-readable label shown in the confirmation prompt for a given tool call.
   * @param {string} name - Tool name
   * @param {object} args - Tool call arguments
   * @returns {string}
   */
  function resolveConfirmationLabel(name, args) {
    if (name === 'navigate') {
      return `navigate to "${args?.value || 'another page'}"`;
    }
    const factory = labelFactories[name];
    if (typeof factory === 'function') return factory(args);
    if (typeof factory === 'string') return factory;
    return name;
  }

  // Watch for a pending tool call dispatched by the store after an LLM response.
  // Claim it synchronously (clearPendingToolCall before any await) to prevent a second
  // composable instance (e.g. briefly active during a page transition) from also handling it.
  watch(
    () => aiStore.pendingToolCall,
    async toolCall => {
      if (!toolCall) return;

      const isNavigate = toolCall.name === 'navigate' && allowScenarioWorkflowNavigation;
      const isCapture = toolCall.name === 'capture_screenshot';
      const isRenderChart = toolCall.name === 'render_chart';
      const isRenderTable = toolCall.name === 'render_table';
      const hasHandler = !!handlers[toolCall.name];

      if (!isNavigate && !isCapture && !isRenderChart && !isRenderTable && !hasHandler) {
        // Tool not registered on this page. Submit an error result so the LLM is informed
        // rather than leaving isHandlingAgentAction stuck (UI showing Stop button forever).
        console.warn(
          `[AI] Unknown tool "${toolCall.name}" — not registered on this page. Returning error result.`
        );
        aiStore.clearPendingToolCall();
        await aiStore.submitToolResult({
          id: toolCall.id,
          name: toolCall.name,
          result: `[Tool] Action failed: tool "${toolCall.name}" is not available on this page. Ask the user to navigate to the correct page first, or suggest an alternative approach.`,
          outcome: 'failed',
        });
        aiStore.assistant.isHandlingAgentAction = false;
        return;
      }

      // Claim: bail if another watcher instance already cleared the pending tool call
      if (aiStore.pendingToolCall !== toolCall) return;
      aiStore.clearPendingToolCall();

      // ── Confirmation gate ──────────────────────────────────────────────────────────
      // Read-only tools and session-approved tools bypass the prompt entirely.
      // Mutating tools require user confirmation before the handler executes.
      const isReadOnly = readOnlyTools.has(toolCall.name);

      if (!isReadOnly) {
        // Session-wide block — decline silently without prompting.
        if (aiStore.sessionAllActionsBlocked || aiStore.sessionExclusions.includes(toolCall.name)) {
          await aiStore.submitToolResult({
            id: toolCall.id,
            name: toolCall.name,
            result:
              '[Tool] User has previously declined this action for this session. Do not attempt it again. Explain to the user what they would need to do manually instead.',
            outcome: 'declined',
          });
          aiStore.assistant.isHandlingAgentAction = false;
          return;
        }

        // Session-wide approval or per-tool approval — proceed without prompting.
        if (
          !aiStore.sessionAllActionsApproved &&
          !aiStore.sessionApprovals.includes(toolCall.name)
        ) {
          // Prompt the user.
          const displayLabel = resolveConfirmationLabel(toolCall.name, toolCall.arguments);
          const choice = await aiStore.requestToolConfirmation(toolCall.name, displayLabel);

          if (choice === 'cancel') {
            // Conversation was cleared or stop was pressed — discard silently.
            aiStore.assistant.isHandlingAgentAction = false;
            return;
          }

          if (choice === 'yes-session-action') aiStore.addSessionApproval(toolCall.name);
          if (choice === 'yes-session-all') aiStore.setSessionAllActionsApproved();

          if (choice === 'no-once') {
            await aiStore.submitToolResult({
              id: toolCall.id,
              name: toolCall.name,
              result:
                '[Tool] User declined this specific request. You may suggest it again if appropriate, or explain how to do it manually.',
              outcome: 'declined',
            });
            aiStore.assistant.isHandlingAgentAction = false;
            return;
          }

          if (choice === 'no-session-action') {
            aiStore.addSessionExclusion(toolCall.name);
            await aiStore.submitToolResult({
              id: toolCall.id,
              name: toolCall.name,
              result:
                '[Tool] User has declined this action for the rest of the session. Remove it from your plans. Explain to the user what they would need to do manually instead.',
              outcome: 'declined',
            });
            aiStore.assistant.isHandlingAgentAction = false;
            return;
          }

          if (choice === 'no-session-all') {
            aiStore.setSessionAllActionsBlocked();
            await aiStore.submitToolResult({
              id: toolCall.id,
              name: toolCall.name,
              result:
                '[Tool] User has declined all agent actions for the rest of this session. Do not attempt any further tool calls. Answer questions in text only.',
              outcome: 'declined',
            });
            aiStore.assistant.isHandlingAgentAction = false;
            return;
          }
          // 'yes-once' or any approved choice — fall through to handler.
        }
      }
      // ── End confirmation gate ──────────────────────────────────────────────────────

      let result;
      let capturedImageData = null;
      try {
        if (toolCall.name === 'navigate') {
          // Navigate receives the string label via arguments.value
          const navLabel = toolCall.arguments?.value ?? '';
          const target = navigationTargets.value.find(t => t.label === navLabel && t.available);
          if (target) {
            const navTarget =
              target.routeName === 'HomePage'
                ? { name: target.routeName }
                : { name: target.routeName, params: route.params };
            await router.push(navTarget);
            // Tick once to let Vue flush: runs the route context reset (resetPageContext) and
            // mounts the new page (setup → setToolSchemasFactory, mounted → enableSidePanel).
            // Without this tick, contextReady may still be true from the previous page when
            // waitForContextReady() is called, causing it to resolve immediately with stale
            // toolSchemasFactory / context.section, resulting in empty tool schemas for the LLM.
            await nextTick();
            try {
              await waitForContextReady();
              // Refresh the schema snapshot to the newly-mounted page's tools.
              // The snapshot was set from the originating page before navigation fired;
              // without updating it here, submitToolResult would send stale schemas and
              // the LLM would see the wrong (or missing) tools for the new page.
              aiStore.pendingToolCallSchemas = aiStore.sidePanel.toolSchemasFactory?.() ?? [];
              result = `[Tool] Action succeeded: navigated to "${navLabel}".`;
            } catch (e) {
              result = `[Tool] Navigation to "${navLabel}" completed but page data did not load in time. Inform the user and ask if they would like to try again.`;
            }
          } else {
            result = `[Tool] Action failed: could not find a section named "${navLabel}". Ask the user to clarify which section they want to navigate to.`;
          }
        } else if (toolCall.name === 'capture_screenshot') {
          capturedImageData = await captureViewport();
          result = capturedImageData
            ? 'Screenshot captured.'
            : '[Tool] Screenshot capture failed — could not capture the screen. Inform the user and continue without visual context.';
        } else if (isRenderChart) {
          const rawChartOptions = toolCall.arguments?.chartOptions ?? {};
          let chartOptions;
          try {
            chartOptions =
              typeof rawChartOptions === 'string' ? JSON.parse(rawChartOptions) : rawChartOptions;
          } catch {
            chartOptions = {};
          }
          aiStore.addChartMessage(chartOptions);
          result = '[Tool] Chart rendered successfully.';
        } else if (isRenderTable) {
          aiStore.addTableMessage(toolCall.arguments ?? {});
          result = '[Tool] Table rendered successfully.';
        } else {
          result = await handlers[toolCall.name](toolCall.arguments ?? {});
        }
      } catch (e) {
        console.error(`[AI agent] Handler error for tool "${toolCall.name}":`, e);
        result =
          '[Tool] Action failed: an unexpected error occurred. Ask the user if they would like to try again.';
      }

      // Guard: handler must return a string. If it returns undefined (e.g. missing return
      // statement), fall back to an error string so isHandlingAgentAction is always cleared.
      if (typeof result !== 'string') {
        console.error(
          `[AI agent] Handler for "${toolCall.name}" did not return a string result. Sending fallback error.`
        );
        result = '[Tool] Action failed: the handler did not return a result. Please try again.';
      }

      if (typeof result === 'string') {
        // Check if the user requested a stop while the handler was running.
        if (aiStore.assistant.isCancelled) {
          await aiStore.sendAssistantMessage(STOP_SUMMARY_PROMPT, { isActionFeedback: true });
          return;
        }
        // Determine step outcome for the completed-step indicator.
        // Strings starting with '[Tool] Action failed:' or '[Tool] Action blocked:' are failures.
        const stepOutcome =
          result.startsWith('[Tool] Action failed:') || result.startsWith('[Tool] Action blocked:')
            ? 'failed'
            : 'success';
        // Submit the result to resume the LLM tool calling loop on the server.
        // For capture_screenshot, also pass the image data URI out-of-band so it can
        // be forwarded to the assistant model without being stored in conversation history.
        try {
          await aiStore.submitToolResult({
            id: toolCall.id,
            name: toolCall.name,
            result,
            outcome: stepOutcome,
            ...(capturedImageData && { imageData: capturedImageData }),
          });
        } catch (e) {
          // The store's catch block handles UI cleanup (spinner, error bubble, isHandlingAgentAction).
          // Guard here ensures the watcher never terminates silently with the UI in a broken state.
          console.error('[AI agent] Unhandled error after submitting tool result:', e);
          aiStore.assistant.isHandlingAgentAction = false;
        }
      }
    }
  );

  return {
    getToolSchemas,
  };
}
